import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Flex,
  HStack,
  VStack,
  Text,
  Image,
  Icon,
  Link,
  IconButton,
  Drawer,
  useDisclosure,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { FaBars } from 'react-icons/fa';
import { getFontById, getMicrositePalette } from './microsite/micrositeUtils';
import { MicrositeRevealProvider } from './microsite/MicrositeScrollReveal.jsx';

const TEMA_STYLES = {
  moderno: {
    navBgScroll: 'rgba(255,255,255,0.95)',
    navBgTop: 'rgba(0,0,0,0.25)',
    navTextScroll: 'gray.700',
    navTextTop: 'white',
    navActiveBgScroll: 'purple.50',
    navActiveBgTop: 'whiteAlpha.200',
    blur: '12px',
    navH: '56px',
    nameWeight: '800',
    nameSpacing: '-0.02em',
    itemRounded: 'full',
    itemPx: 3,
    footerBorderColor: 'gray.100',
  },
  clasico: {
    navBgScroll: null,
    navBgTop: null,
    navTextScroll: 'white',
    navTextTop: 'white',
    navActiveBgScroll: 'whiteAlpha.200',
    navActiveBgTop: 'whiteAlpha.200',
    blur: '0px',
    navH: '52px',
    nameWeight: '700',
    nameSpacing: '0',
    itemRounded: 'md',
    itemPx: 3,
    footerBorderColor: 'gray.200',
  },
  elegante: {
    navBgScroll: 'rgba(255,255,255,0.98)',
    navBgTop: 'rgba(255,255,255,0.98)',
    navTextScroll: 'gray.700',
    navTextTop: 'gray.700',
    navActiveBgScroll: 'transparent',
    navActiveBgTop: 'transparent',
    blur: '0px',
    navH: '50px',
    nameWeight: '400',
    nameSpacing: '0.1em',
    itemRounded: 'none',
    itemPx: 3,
    footerBorderColor: 'gray.200',
  },
  aurora: {
    navBgScroll: 'rgba(15,23,42,0.94)',
    navBgTop: 'rgba(15,23,42,0.72)',
    navTextScroll: 'gray.100',
    navTextTop: 'white',
    navActiveBgScroll: 'whiteAlpha.100',
    navActiveBgTop: 'whiteAlpha.200',
    blur: '14px',
    navH: '56px',
    nameWeight: '700',
    nameSpacing: '-0.02em',
    itemRounded: 'lg',
    itemPx: 3,
    footerBorderColor: 'whiteAlpha.100',
  },
  editorial: {
    navBgScroll: 'rgba(255,255,255,0.98)',
    navBgTop: 'rgba(250,247,242,0.96)',
    navTextScroll: 'gray.800',
    navTextTop: 'gray.800',
    navActiveBgScroll: 'blackAlpha.50',
    navActiveBgTop: 'blackAlpha.50',
    blur: '12px',
    navH: '56px',
    nameWeight: '600',
    nameSpacing: '-0.01em',
    itemRounded: 'none',
    itemPx: 3,
    footerBorderColor: 'gray.200',
  },
  pulso: {
    navBgScroll: '#000000',
    navBgTop: '#000000',
    navTextScroll: 'white',
    navTextTop: 'white',
    navActiveBgScroll: 'whiteAlpha.200',
    navActiveBgTop: 'whiteAlpha.200',
    blur: '0px',
    navH: '56px',
    nameWeight: '900',
    nameSpacing: '0.06em',
    itemRounded: 'none',
    itemPx: 3,
    footerBorderColor: 'gray.800',
  },
  vortex: {
    navBgScroll: 'rgba(2,6,23,0.94)',
    navBgTop: 'rgba(2,6,23,0.72)',
    navTextScroll: 'cyan.100',
    navTextTop: 'white',
    navActiveBgScroll: 'whiteAlpha.150',
    navActiveBgTop: 'whiteAlpha.200',
    blur: '16px',
    navH: '56px',
    nameWeight: '800',
    nameSpacing: '-0.02em',
    itemRounded: 'xl',
    itemPx: 3,
    footerBorderColor: 'whiteAlpha.100',
  },
  glass: {
    navBgScroll: 'rgba(255,255,255,0.75)',
    navBgTop: 'rgba(255,255,255,0.45)',
    navTextScroll: 'gray.800',
    navTextTop: 'gray.800',
    navActiveBgScroll: 'whiteAlpha.700',
    navActiveBgTop: 'whiteAlpha.500',
    blur: '18px',
    navH: '56px',
    nameWeight: '700',
    nameSpacing: '-0.01em',
    itemRounded: 'full',
    itemPx: 3,
    footerBorderColor: 'gray.200',
  },
  kinetic: {
    navBgScroll: 'rgba(17,24,39,0.95)',
    navBgTop: 'rgba(17,24,39,0.82)',
    navTextScroll: 'rose.100',
    navTextTop: 'white',
    navActiveBgScroll: 'whiteAlpha.200',
    navActiveBgTop: 'whiteAlpha.200',
    blur: '10px',
    navH: '56px',
    nameWeight: '800',
    nameSpacing: '0.01em',
    itemRounded: 'lg',
    itemPx: 3,
    footerBorderColor: 'gray.700',
  },
};

const MicrositeLayout = ({ bandaData, children, micrositeConfig, sections = [], tema = 'moderno' }) => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('inicio');
  const { open: isOpen, onOpen, onClose } = useDisclosure();

  const style = TEMA_STYLES[tema] || TEMA_STYLES.moderno;
  const primaryColor = getMicrositePalette(micrositeConfig || {}, tema).primario;
  const fontConfig = getFontById(micrositeConfig?.tipografia);

  useEffect(() => {
    if (!fontConfig.url) return;
    const id = `gfont-${fontConfig.id}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = fontConfig.url;
    document.head.appendChild(link);
  }, [fontConfig]);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      const offsets = sections.map(s => {
        const el = document.getElementById(s.id);
        return el ? { id: s.id, top: el.offsetTop - 100 } : null;
      }).filter(Boolean);
      const scrollPos = window.scrollY + 150;
      for (let i = offsets.length - 1; i >= 0; i--) {
        if (scrollPos >= offsets[i].top) { setActiveSection(offsets[i].id); break; }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [sections]);

  const scrollTo = (id) => {
    onClose();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const navBg = tema === 'clasico'
    ? (scrolled ? primaryColor : primaryColor)
    : (scrolled ? style.navBgScroll : style.navBgTop);
  const navText = scrolled ? style.navTextScroll : style.navTextTop;
  const navActiveBg = scrolled ? style.navActiveBgScroll : style.navActiveBgTop;

  const isElegante = tema === 'elegante';
  const isEditorial = tema === 'editorial';

  /**
   * Menú sin scroll: el tamaño escala con el ancho del contenedor (cqi).
   * Si el navegador no soporta cqi, se usa vw como aproximación.
   */
  const navItemSxElegante = {
    whiteSpace: 'nowrap',
    fontSize: 'clamp(0.5rem, 0.2rem + 2.85cqi, 0.75rem)',
    paddingInline: 'clamp(2px, 0.3cqi, 8px)',
    letterSpacing: '0.06em',
    '@supports not (width: 1cqi)': {
      fontSize: 'clamp(0.55rem, 0.35rem + 1.15vw, 0.75rem)',
      paddingInline: 'clamp(3px, 0.45vw, 8px)',
    },
  };
  const navItemSxDefault = {
    whiteSpace: 'nowrap',
    fontSize: 'clamp(0.5rem, 0.2rem + 3cqi, 0.8125rem)',
    paddingInline: 'clamp(2px, 0.35cqi, 10px)',
    '@supports not (width: 1cqi)': {
      fontSize: 'clamp(0.55rem, 0.38rem + 1.2vw, 0.8125rem)',
      paddingInline: 'clamp(3px, 0.5vw, 10px)',
    },
  };

  const renderNavItem = (s) => {
    const isActive = activeSection === s.id;
    if (isElegante) {
      return (
        <Text
          key={s.id}
          fontWeight="400"
          color={navText}
          cursor="pointer"
          py={1.5}
          textTransform="uppercase"
          borderBottom={isActive ? '1px solid' : '1px solid transparent'}
          borderColor={isActive ? 'gray.700' : 'transparent'}
          _hover={{ borderColor: 'gray.400' }}
          transition="all 0.2s"
          sx={navItemSxElegante}
          onClick={() => scrollTo(s.id)}
        >
          {s.label}
        </Text>
      );
    }
    return (
      <Text
        key={s.id}
        fontWeight={isActive ? '700' : '500'}
        color={navText}
        cursor="pointer"
        py={1.5}
        rounded={style.itemRounded}
        bg={isActive ? navActiveBg : 'transparent'}
        _hover={{ bg: navActiveBg }}
        transition="all 0.2s"
        sx={navItemSxDefault}
        onClick={() => scrollTo(s.id)}
      >
        {s.label}
      </Text>
    );
  };

  const renderDrawerItem = (s) => {
    const isActive = activeSection === s.id;
    if (isElegante) {
      return (
        <Text
          key={s.id} fontSize="sm" fontWeight={isActive ? '500' : '400'}
          color={isActive ? 'gray.900' : 'gray.600'}
          cursor="pointer" px={4} py={3}
          textTransform="uppercase" letterSpacing="0.08em"
          borderLeft={isActive ? '2px solid' : '2px solid transparent'}
          borderColor={isActive ? 'gray.900' : 'transparent'}
          _hover={{ bg: 'gray.50' }}
          onClick={() => scrollTo(s.id)}
        >
          {s.label}
        </Text>
      );
    }
    const activeColor = primaryColor;
    const activeBg =
      tema === 'clasico' ? `${primaryColor}18` : tema === 'pulso' ? 'whiteAlpha.100' : 'purple.50';
    return (
      <Text
        key={s.id} fontSize="md" fontWeight={isActive ? '700' : '500'}
        color={isActive ? activeColor : 'gray.700'}
        cursor="pointer" px={4} py={3} rounded="lg"
        bg={isActive ? activeBg : 'transparent'}
        _hover={{ bg: 'gray.50' }}
        onClick={() => scrollTo(s.id)}
      >
        {s.label}
      </Text>
    );
  };

  return (
    <MicrositeRevealProvider micrositeConfig={micrositeConfig}>
      <Box minH="100vh" display="flex" flexDirection="column" fontFamily={fontConfig.family || undefined}>
        {/* Navbar */}
        <Box
          position="fixed" top={0} left={0} right={0} zIndex={1000}
          bg={navBg}
          backdropFilter={style.blur !== '0px' ? `blur(${style.blur})` : undefined}
          borderBottom={isElegante || isEditorial ? '1px solid' : (scrolled ? '1px solid' : 'none')}
          borderColor="gray.200"
          transition="all 0.3s ease"
          boxShadow={scrolled && !isElegante ? 'sm' : 'none'}
        >
          {/* Ancho completo: marca pegada al borde izquierdo del viewport (solo margen ps); menú con margen derecho */}
          <Box w="100%" ps={{ base: 3, md: 4, lg: 5 }} pe={{ base: 4, md: 6, lg: 8 }}>
            <Flex
              w="100%"
              align="center"
              justify="flex-start"
              gap={{ base: 3, md: 8, lg: 10 }}
              minH={style.navH}
              minW={0}
              py={{ base: 1.5, md: 2 }}
              h={{ md: 'auto' }}
            >
              {/* Marca: izquierda del viewport; no encoge; separada del bloque de enlaces */}
              <HStack
                gap={2}
                justify="flex-start"
                cursor="pointer"
                onClick={() => scrollTo('inicio')}
                flexShrink={0}
                minW={0}
                maxW={{ base: 'calc(100% - 48px)', md: 'min(34vw, 320px)', lg: 'min(38vw, 400px)' }}
              >
                {bandaData?.avatar ? (
                  <Avatar.Root
                    size="sm"
                    flexShrink={0}
                    borderWidth="1px"
                    borderColor={
                      scrolled
                        ? 'gray.200'
                        : tema === 'moderno'
                          ? 'whiteAlpha.400'
                          : tema === 'aurora'
                            ? 'whiteAlpha.300'
                            : 'gray.200'
                    }><Avatar.Fallback name={bandaData.nombre} /><Avatar.Image src={bandaData.avatar} /></Avatar.Root>
                ) : null}
                <Text
                  fontWeight={style.nameWeight} fontSize={isElegante ? 'sm' : { base: 'md', md: 'lg' }}
                  color={navText}
                  transition="color 0.3s"
                  letterSpacing={style.nameSpacing}
                  textTransform={isElegante ? 'uppercase' : 'none'}
                  fontFamily={
                    fontConfig.family
                      || (tema === 'clasico' || tema === 'editorial' ? "'Georgia', 'Times New Roman', serif" : undefined)
                  }
                  lineClamp={1}
                  lineHeight="1.2"
                  textAlign="left"
                >
                  {bandaData?.nombre || ''}
                </Text>
              </HStack>

              {/* Enlaces: sin scroll; una línea (texto fluido con cqi) o dos líneas solo si el hueco del menú es estrecho */}
              <Box
                flex="1"
                minW={0}
                display={{ base: 'none', md: 'block' }}
                css={{
                  containerType: 'inline-size',
                  containerName: 'msnav'
                }}
              >
                <Flex
                  w="100%"
                  justifyContent="center"
                  alignItems="center"
                  alignContent="center"
                  flexWrap="nowrap"
                  rowGap={2}
                  columnGap="clamp(2px, 0.4cqi, 8px)"
                  py={1}
                  px={1}
                  css={{
                    '& @supports not (width: 1cqi)': {
                      columnGap: 'clamp(2px, 0.3vw, 8px)',
                    },

                    '& @container msnav (max-width: 34rem)': {
                      flexWrap: 'wrap',
                    }
                  }}
                >
                  {sections.map(s => renderNavItem(s))}
                </Flex>
              </Box>

              <IconButton
                ml={{ base: 'auto', md: 0 }}
                display={{ base: 'inline-flex', md: 'none' }}
                flexShrink={0}
                variant="ghost"
                color={navText}
                onClick={(e) => { e.stopPropagation(); onOpen(); }}
                aria-label="Menu"
                _hover={{ bg: navActiveBg }}><Icon asChild><FaBars /></Icon></IconButton>
            </Flex>
          </Box>
        </Box>

        {/* Mobile drawer */}
        <Drawer.Root open={isOpen} placement='end' onOpenChange={e => {
          if (!e.open) {
            onClose();
          }
        }}>
          <Portal>

            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content>
                <Drawer.CloseTrigger />
                <Drawer.Body pt={12}>
                  <VStack gap={2} align="stretch">
                    {sections.map(s => renderDrawerItem(s))}
                  </VStack>
                </Drawer.Body>
              </Drawer.Content>
            </Drawer.Positioner>

          </Portal>
        </Drawer.Root>

        {/* Main content */}
        <Box flex="1">{children}</Box>

        {/* Footer */}
        <Box py={6} borderTop="1px solid" borderColor={style.footerBorderColor}>
          <Container maxW="6xl">
            <Flex justify="center" align="center">
              <Link
                href="https://cadencya.com"
                _hover={{ textDecoration: 'none', opacity: 1 }}
                target='_blank'
                rel='noopener noreferrer'>
                <HStack gap={2} opacity={0.5} _hover={{ opacity: 0.8 }} transition="opacity 0.2s">
                  <Text fontSize="xs" color="gray.400">Powered by</Text>
                  <Image src="https://cadencya.com/logo%20y%20texto%20cadencya.png" alt="Cadencya" h="16px" w="auto" />
                </HStack>
              </Link>
            </Flex>
          </Container>
        </Box>
      </Box>
    </MicrositeRevealProvider>
  );
};

export default MicrositeLayout;
